import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Container,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpOutlineIcon,
  OpenInNew as OpenInNewIcon,
  ReportProblem as ReportProblemIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  AddCircleOutline as AddCircleOutlineIcon,
} from '@mui/icons-material';

import { searchCompanies } from '../../api/companies';
import { getCompanyPipelineComparison } from '../../api/companyPipelines';
import { CompanyListItem } from '../../types/company';
import {
  ComparisonStatus,
  PipelineComparisonResponse,
  PipelineComparisonRow,
} from '../../types/companyPipeline';

type StatusFilter = ComparisonStatus | 'all';

const STATUS_META: Record<
  ComparisonStatus,
  { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default'; icon: React.ReactElement }
> = {
  match: {
    label: 'Match',
    color: 'success',
    icon: <CheckCircleIcon fontSize="small" />,
  },
  stage_mismatch: {
    label: 'Stage mismatch',
    color: 'warning',
    icon: <ReportProblemIcon fontSize="small" />,
  },
  indication_mismatch: {
    label: 'Indication mismatch',
    color: 'warning',
    icon: <ReportProblemIcon fontSize="small" />,
  },
  missing_in_sozo: {
    label: 'Missing in SOZO',
    color: 'error',
    icon: <AddCircleOutlineIcon fontSize="small" />,
  },
  extra_in_sozo: {
    label: 'Extra in SOZO',
    color: 'info',
    icon: <RemoveCircleOutlineIcon fontSize="small" />,
  },
  needs_review: {
    label: 'Needs review',
    color: 'default',
    icon: <HelpOutlineIcon fontSize="small" />,
  },
};

// Order tiles by how much attention each bucket deserves.
const SUMMARY_ORDER: ComparisonStatus[] = [
  'missing_in_sozo',
  'stage_mismatch',
  'indication_mismatch',
  'extra_in_sozo',
  'needs_review',
  'match',
];

const SUMMARY_KEY: Record<ComparisonStatus, keyof PipelineComparisonResponse['summary']> = {
  match: 'matches',
  stage_mismatch: 'stage_mismatches',
  indication_mismatch: 'indication_mismatches',
  missing_in_sozo: 'missing_in_sozo',
  extra_in_sozo: 'extra_in_sozo',
  needs_review: 'needs_review',
};

const StatusChip: React.FC<{ status: ComparisonStatus }> = ({ status }) => {
  const meta = STATUS_META[status];
  return (
    <Chip
      size="small"
      color={meta.color}
      icon={meta.icon}
      label={meta.label}
      variant={status === 'match' ? 'outlined' : 'filled'}
    />
  );
};

const SummaryTile: React.FC<{
  label: string;
  value: number;
  active: boolean;
  onClick: () => void;
}> = ({ label, value, active, onClick }) => (
  <Paper
    onClick={onClick}
    sx={{
      p: 2,
      minWidth: 150,
      cursor: 'pointer',
      borderWidth: active ? 2 : 1,
      borderStyle: 'solid',
      borderColor: active ? 'primary.main' : 'divider',
      '&:hover': { borderColor: 'primary.light' },
    }}
  >
    <Typography variant="h4" sx={{ fontWeight: 700 }}>
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Paper>
);

/** Dash for empty values so blank cells never read as "we checked and it is blank". */
const cell = (value?: string | null): string => (value && value.trim() ? value : '—');

const CompanyPipelinesPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<CompanyListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [company, setCompany] = useState<CompanyListItem | null>(null);

  const [urlOverride, setUrlOverride] = useState('');
  const [data, setData] = useState<PipelineComparisonResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');

  // Guards against a slow earlier search overwriting a newer one.
  const searchSeq = useRef(0);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setOptions([]);
      return;
    }
    const seq = ++searchSeq.current;
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const result = await searchCompanies(term, 20);
        if (seq === searchSeq.current) {
          setOptions(result.results || []);
        }
      } catch {
        if (seq === searchSeq.current) {
          setOptions([]);
        }
      } finally {
        if (seq === searchSeq.current) {
          setSearching(false);
        }
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  const runComparison = useCallback(
    async (target: CompanyListItem, overrideUrl?: string) => {
      setLoading(true);
      setError(null);
      setData(null);
      try {
        const result = await getCompanyPipelineComparison(
          target.id,
          overrideUrl?.trim() || undefined,
        );
        setData(result);
        setFilter('all');
      } catch (e: any) {
        setError(
          e?.response?.data?.detail ||
            'Failed to compare this pipeline. Please try again.',
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const handleSelect = useCallback(
    (value: CompanyListItem | null) => {
      setCompany(value);
      setUrlOverride('');
      setData(null);
      setError(null);
      if (value) {
        runComparison(value);
      }
    },
    [runComparison],
  );

  const visibleRows = useMemo(() => {
    if (!data) return [];
    if (filter === 'all') return data.rows;
    return data.rows.filter((r) => r.status === filter);
  }, [data, filter]);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Company Pipelines
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Compare the pipeline recorded in SOZO against the pipeline the company
        publishes on its own website.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Autocomplete
            sx={{ flex: 1, minWidth: 280 }}
            options={options}
            loading={searching}
            value={company}
            onChange={(_, value) => handleSelect(value)}
            onInputChange={(_, value) => setQuery(value)}
            getOptionLabel={(option) => option.name || option.id}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search company"
                placeholder="Start typing a company name…"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            sx={{ flex: 1, minWidth: 280 }}
            label="Pipeline URL override (optional)"
            placeholder="https://example.com/pipeline"
            value={urlOverride}
            onChange={(e) => setUrlOverride(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && company) {
                runComparison(company, urlOverride);
              }
            }}
            helperText="Use when the page we found is not the real pipeline page."
          />
        </Stack>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {data && !loading && (
        <>
          {data.fetch_error ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <strong>Website pipeline unavailable.</strong> {data.fetch_error}.
              SOZO rows below are shown as “needs review” — we could not verify
              them either way, so none of them are being reported as extra. Try
              entering the pipeline URL directly above.
            </Alert>
          ) : (
            data.website_url && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Compared against{' '}
                <Link href={data.website_url} target="_blank" rel="noopener">
                  {data.website_url} <OpenInNewIcon sx={{ fontSize: 12 }} />
                </Link>
                {data.source_url_origin === 'discovered' &&
                  ' (found automatically — check it is the right page)'}
                . Website rows: {data.summary.total_website_rows}, SOZO rows:{' '}
                {data.summary.total_sozo_rows}.
              </Alert>
            )
          )}

          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <SummaryTile
              label="All rows"
              value={data.rows.length}
              active={filter === 'all'}
              onClick={() => setFilter('all')}
            />
            {SUMMARY_ORDER.map((status) => (
              <SummaryTile
                key={status}
                label={STATUS_META[status].label}
                value={data.summary[SUMMARY_KEY[status]] as number}
                active={filter === status}
                onClick={() => setFilter(status)}
              />
            ))}
          </Stack>

          <Paper>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 170 }}>Status</TableCell>
                    <TableCell>Therapeutic (SOZO)</TableCell>
                    <TableCell>Therapeutic (Website)</TableCell>
                    <TableCell>Stage (SOZO)</TableCell>
                    <TableCell>Stage (Website)</TableCell>
                    <TableCell>Indication (SOZO)</TableCell>
                    <TableCell>Indication (Website)</TableCell>
                    <TableCell sx={{ minWidth: 240 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row: PipelineComparisonRow, idx: number) => (
                    <TableRow key={`${row.status}-${idx}`} hover>
                      <TableCell>
                        <StatusChip status={row.status} />
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            row.sozo?.company_code_name ||
                            row.sozo?.generic_name ||
                            row.sozo?.brand_name ||
                            ''
                          }
                        >
                          <span>{cell(row.sozo?.therapeutic_name)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{cell(row.website?.therapeutic_name)}</TableCell>
                      <TableCell>{cell(row.sozo?.phase)}</TableCell>
                      <TableCell>
                        {cell(
                          row.website?.normalized_stage || row.website?.stage,
                        )}
                      </TableCell>
                      <TableCell>{cell(row.sozo?.disease_name)}</TableCell>
                      <TableCell>{cell(row.website?.indication)}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {row.notes.join(' ')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {visibleRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 3, textAlign: 'center' }}
                        >
                          No rows in this category.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {!data && !loading && !error && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Search for a company to compare its pipeline.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default CompanyPipelinesPage;
